import { useCallback, useMemo, useRef } from 'react';
import { clampPosition } from '../helpers/clamp-position';
import { getInitialOrderFromStops } from '../helpers/get-initial-order-from-stops';
import { getOrderByStopIndex } from '../helpers/get-order-by-stop-index';
import { getOrderIndices } from '../helpers/get-order-indices';
import { swapOrderOnCross } from '../helpers/swap-order-on-cross';

const clampPositions = (positions) => positions.map(clampPosition);

const arePositionsEqual = (left, right) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areIdsEqual = (left, right) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const getStopMap = (stops) => new Map(stops.map((stop) => [stop.id, stop]));

const shouldResetHandleOrder = (currentOrder, stops, stopMap) =>
  currentOrder.length !== stops.length || currentOrder.some((stopId) => !stopMap.has(stopId));

const getSortedStops = (order, stopMap) =>
  order.map((stopId) => stopMap.get(stopId)).filter((stop) => stop != null);

const emitDragChanges = ({
  previousPositions,
  nextPositions,
  stopOrder,
  initialStopId,
  onDrag,
}) => {
  const orderByStopIndex = getOrderByStopIndex(nextPositions);
  nextPositions.forEach((nextPosition, stopIndex) => {
    if (previousPositions[stopIndex] === nextPosition) {
      return;
    }

    onDrag?.({
      stopId: stopOrder[stopIndex],
      position: nextPosition,
      orderIndex: orderByStopIndex[stopIndex],
      initialId: initialStopId,
    });
  });
};

const useStopsSliderState = ({ stops, onDragStart, onDrag, onDragEnd }) => {
  const previousPositionsRef = useRef([]);
  const handleOrderRef = useRef([]);
  const dragStartStopIdRef = useRef(undefined);

  const stopMap = useMemo(() => getStopMap(stops), [stops]);

  // Sync refs during render when stops/length change so sortedStops and positions use the new order in the same commit.
  if (shouldResetHandleOrder(handleOrderRef.current, stops, stopMap)) {
    handleOrderRef.current = getInitialOrderFromStops(stops);
  }

  // When stops are updated externally (e.g. "flip" action), ids stay the same but
  // their left-to-right order can change. If we don't resync, rc-slider's handle index
  // will map to the wrong stop id.
  if (dragStartStopIdRef.current == null) {
    const nextInitialOrder = getInitialOrderFromStops(stops);

    if (!areIdsEqual(handleOrderRef.current, nextInitialOrder)) {
      handleOrderRef.current = nextInitialOrder;
    }
  }

  // Derive sorted stops and positions for current render.
  const sortedStops = getSortedStops(handleOrderRef.current, stopMap);
  const positions = clampPositions(sortedStops.map((stop) => stop.position));

  if (
    previousPositionsRef.current.length !== positions.length ||
    (dragStartStopIdRef.current == null &&
      !arePositionsEqual(previousPositionsRef.current, positions))
  ) {
    previousPositionsRef.current = positions;
  }

  const isDraggingRef = useRef(false);

  const handleCaptureStopId = useCallback((stopId) => {
    dragStartStopIdRef.current = stopId;
  }, []);

  const handleInteractionStart = useCallback(
    // eslint-disable-next-line yola/@typescript-eslint/no-unused-vars
    (rawPositions) => {
      if (isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = true;

      const stopId = dragStartStopIdRef.current;

      if (stopId) {
        onDragStart?.({ stopId });
      }
    },
    [onDragStart]
  );

  const handleInteraction = useCallback(
    (rawPositions) => {
      const previousPositions = previousPositionsRef.current;

      if (previousPositions.length !== rawPositions.length) {
        previousPositionsRef.current = clampPositions(rawPositions);

        return;
      }

      const nextPositions = clampPositions(rawPositions);
      const initialStopId = dragStartStopIdRef.current;
      const nextOrder = swapOrderOnCross(
        previousPositions,
        nextPositions,
        handleOrderRef.current,
        initialStopId
      );

      if (nextOrder !== handleOrderRef.current) {
        handleOrderRef.current = nextOrder;
      }

      emitDragChanges({
        previousPositions,
        nextPositions,
        stopOrder: handleOrderRef.current,
        initialStopId,
        onDrag,
      });

      previousPositionsRef.current = nextPositions;
    },
    [onDrag]
  );

  const handleInteractionEnd = useCallback(
    (rawPositions) => {
      const nextPositions = clampPositions(rawPositions);
      const nextOrder = getOrderIndices(nextPositions).map((i) => handleOrderRef.current[i]);

      handleOrderRef.current = nextOrder;
      previousPositionsRef.current = nextPositions;
      onDragEnd?.({ order: nextOrder });
      dragStartStopIdRef.current = undefined;

      // Defer reset so the focus event rc-slider fires synchronously on mouseup
      Promise.resolve().then(() => {
        isDraggingRef.current = false;
      });
    },
    [onDragEnd]
  );

  return {
    sortedStops,
    positions,
    handleCaptureStopId,
    handleInteractionStart,
    handleInteraction,
    handleInteractionEnd,
  };
};

export default useStopsSliderState;
